import type { MantineThemeOverride } from '@mantine/core';
import type { defaultConfig as TamaguiThemeOverride } from '@tamagui/config/v4';
import type { Col, Color } from '@epihub/web/src/color';
import { type Dimension, type Screen } from '@epihub/web/src/dimensions';
import { expectMobileDeps, expectWebDeps } from '@epihub/web/src/_ctx/deps';
import { type ColorScheme } from '../types';
import { type Producer, ThemeBuilder } from '../builder';
import { enhanceThemeDefV0WithMantine } from './enhanceThemeDefV0WithMantine';
import { enhanceThemeDefV0WithTamagui } from './enhanceThemeDefV0WithTamagui';

export type BuildThemeV0 = {
  Creator: {
    mantine?: MantineThemeOverride;
    tamagui?: typeof TamaguiThemeOverride;
    colors: {
      [K: string]: Col;
    };
  };
  State: {
    __version: 'v0';
    scheme: ColorScheme;
  };
  Theme: {
    __version: 'v0';
    scheme: ColorScheme;
    mantine: MantineThemeOverride;
    tamagui: typeof TamaguiThemeOverride;
    font: {
      family: {
        base: string[];
        monospace: string[];
      };
      header: { size: Dimension };
      base: { size: Dimension };
    };
    screen: Screen;
    col:
      & {
        epilogo: Color;
        epilogoStrong: Color;
        epilogoAccent: Color;

        light: Color;
        dark: Color;
        theme: Color;
        accent: Color;

        primary: Color;
        secondary: Color;
        tertiary: Color;

        error: Color;
        success: Color;
        warning: Color;
        info: Color;
        danger: Color;

        text: Color;
        textSecondary: Color;
        textTertiary: Color;
        textDimmed: Color;
        textPlaceholder: Color;
        link: Color;

        background: Color;
        backgroundSecondary: Color;
        backgroundTertiary: Color;

        border: Color;
        disabled: Color;
      }
      & Record<string, Color>;
  };
};

export function buildThemeV0<
  Creator extends BuildThemeV0['Creator'],
  State extends BuildThemeV0['State'],
  ProducerResult extends object = {},
>(
  creator: Omit<Creator, 'colors'> & { colors?: Creator['colors'] },
  producer: Producer<
    Creator,
    State,
    & ProducerResult
    & { __version?: 'v0' }
    & { scheme?: ColorScheme }
    & { mantine?: MantineThemeOverride }
    & { font?: Partial<BuildThemeV0['Theme']['font']> }
    & { screen?: Screen }
    & { col?: Partial<BuildThemeV0['Theme']['col']> }
  >,
) {
  const creatorWithDefaults = {
    ...creator,
    colors: {
      ...creator.colors,
    },
  } as Creator;

  return new ThemeBuilder<Creator, State, BuildThemeV0['Theme'] & ProducerResult>(
    'v0',
    creatorWithDefaults,
    (creator: Creator, state: State) => {
      const base = producer(creator, state);

      (base as any).__version = 'v0';
      (base as any).scheme = (base as any).scheme ?? state.scheme ?? 'light';

      if (creator.mantine) {
        expectWebDeps();
        enhanceThemeDefV0WithMantine({
          theme: base as any,
          creator,
          state,
        });
      } else if (creator.tamagui) {
        expectMobileDeps();
        enhanceThemeDefV0WithTamagui({
          theme: base as any,
          creator,
          state,
        });
      }

      return base as BuildThemeV0['Theme'] & ProducerResult;
    },
  );
}
